import { useCallback, useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverAnchor } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { Plus, CalendarPlus, Gavel, Clock3, Pencil, Video, Phone, MoreHorizontal, MapPin, FileSearch, Sparkles, Loader2, ChevronDown, Paperclip, Users, Building2, Bell, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Recolhivel from "@/components/Recolhivel";
import AutosTag, { autosLabel } from "@/components/AutosTag";
import { invalidarProcessosMapa } from "@/hooks/useProcessosMapa";
import SugestaoInput from "@/components/SugestaoInput";
import { Dica, Info } from "@/components/Dica";
import {
  maskDateInput, maskTimeInput, dateBrToIso, isoToDateBr, isValidTime, maskCaseNumberInput,
  dateBrToJsDate, jsDateToDateBr,
} from "@/lib/dateTimeMask";
import { CASE_AREAS, PARTY_LABELS, KNOWN_CASE_AREAS } from "@/lib/caseAreas";
import { brDatesInText } from "@/lib/format";
import PrazoCalculadora from "@/components/PrazoCalculadora";
import { calcularPrazo, PRAZO_PADRAO_DIAS, PRAZO_PADRAO_HORA } from "@/lib/prazoPadrao";
import { PRESETS } from "@/lib/prazoPresets";
import { subtipoCompromisso, SUBTIPO_COR } from "@/lib/compromisso";

import { SEL_CINZA } from "@/lib/selecao";
// Lote 111 (Robson, 24/10/2026) — padrão visual: tudo que é SUGESTÃO (prazo padrão, calculadora, atalho, IA)
// aparece em azul claro até o usuário digitar por cima.
const SUG_FIELD = "!bg-[#f3f9ff] !border-[#8cc7f5]";
// Compromisso: atalhos de tipo (preenchem o campo "Tipo", que continua livre para outro nome).
const COMPROMISSO_TIPOS = ["Reunião", "Perícia", "Lembrete"];
// Número CNJ ...J.TR...: J=5 é Justiça do Trabalho — ajuda a mostrar os prazos prontos certos ao agendar de uma publicação.
const inferCaseArea = (num) => (/^\d{7}-?\d{2}\.?\d{4}\.?5\./.test(String(num || "")) ? "trabalhista" : null);

// Lote 71 (pedido do Robson, 09/10/2026) — campo único "Lembrete" entre Data/Hora e Descrição,
// no estilo do Calendário da Apple: aviso X antes do horário do evento OU "Todos os dias"
// (repete diariamente até marcar como feito/data-limite — antiga caixa "Repetir diariamente").
// Vale para qualquer tipo (audiência, prazo, outro). Valor "" = sem lembrete extra.
const REMINDER_OPTIONS = [
  { value: "", label: "Nenhum" },
  { value: "0", label: "No horário do compromisso" },
  { value: "5", label: "5 minutos antes" },
  { value: "10", label: "10 minutos antes" },
  { value: "15", label: "15 minutos antes" },
  { value: "30", label: "30 minutos antes" },
  { value: "60", label: "1 hora antes" },
  { value: "120", label: "2 horas antes" },
  { value: "1440", label: "1 dia antes" },
  { value: "daily", label: "Todos os dias (repetir diariamente)" },
];
const reminderFromHearing = (h) =>
  h?.recurring_daily ? "daily"
    : (h?.reminder_minutes_before !== undefined && h?.reminder_minutes_before !== null
        ? String(h.reminder_minutes_before) : "");

// "Reunião" virou "Outro" (Lote 56, pedido do Robson, 30/09/2026) — cobre qualquer
// compromisso que não seja audiência nem prazo (perícia, diligência, reunião, etc.), com
// o tipo específico preenchido no campo de texto livre abaixo (era "Assunto", ver mais
// adiante). O VALOR interno continua "reuniao" de propósito — trocar pra outro valor
// (ex.: "outro") exigiria migrar todo evento "reuniao" já salvo no banco e todo lugar do
// backend que filtra por esse valor (lembrete de ligação, e-mails, etc.); só o texto que
// aparece pro usuário muda.
const TYPES = [
  { value: "prazo", label: "Prazo", icon: Clock3 },
  { value: "audiencia", label: "Audiência", icon: Gavel },
  { value: "reuniao", label: "Compromisso", icon: MoreHorizontal },
];

// CASE_AREAS/PARTY_LABELS agora vêm de @/lib/caseAreas (compartilhado com
// ProcessoDetail.jsx e Processos.jsx, ver comentário lá — mesma tabela do backend
// server.py: PARTY_LABELS/CASE_AREA_LABEL).

// Fase da audiência (Lote 25, pedido do Robson) — só faz sentido pra event_type "audiencia".
const PHASES = [
  { value: "inicial", label: "Inicial" },
  { value: "instrucao", label: "Instrução" },
  { value: "julgamento", label: "Julgamento" },
  { value: "outro", label: "Outro" },
];

// Opções de lembrete por ligação PARA ESSE EVENTO específico (Lote 47, pedido do Robson em
// 24/09/2026: "criar a possibilidade de ativar e configurar durante o próprio agendamento
// do evento"). "default" segue o que estiver em Configurações > Aviso por ligação
// telefônica; "on" força uma ligação só pra esse compromisso (mesmo que o canal esteja
// desligado em Configurações), X minutos antes; "off" nunca liga pra esse compromisso,
// mesmo com o canal ligado em Configurações.
const CALL_REMINDER_MODES = [
  { value: "default", label: "Padrão" },
  { value: "on", label: "Ativar" },
  { value: "off", label: "Desativar" },
];

// Hora atual no formato "HH:MM", usada para pré-preencher o campo Hora de um evento novo —
// evita o bug do <input type="time"> nativo (Safari) que só "avisa" o React que tem valor
// depois que o usuário mexe ativamente nele, deixando o botão "Salvar e agendar" com
// aparência de desabilitado mesmo quando o resto do formulário já está preenchido.
const nowTime = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function NewEventDialog({
  onCreated, existing = null, trigger = null, initialDate = null, initialTime = null,
  // Lote 76: uso controlado + dados iniciais (ex.: "Agendar prazo" a partir de uma publicação).
  open: controlledOpen = undefined, onOpenChange: controlledOnOpenChange = undefined,
  hideTrigger = false, prefill = null,
}) {
  const isEdit = Boolean(existing);
  const [openInner, setOpenInner] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : openInner;
  const setOpen = controlledOnOpenChange || setOpenInner;
  const [autoRun, setAutoRun] = useState(false);
  const [clients, setClients] = useState([]);
  // Lote 116 (Robson, 24/10/2026): atalho para escolher um processo monitorado no Novo evento.
  const [procs, setProcs] = useState([]);
  const [buscaProc, setBuscaProc] = useState(null);   // null = fechado; string = busca aberta
  // Lote 117 — regra de ouro: ao identificar o número de um processo, buscar o cadastro e os agendamentos que já existem.
  const navigate = useNavigate();
  const [regra, setRegra] = useState(null);             // resposta de /processo-por-numero
  const [regraIntegrado, setRegraIntegrado] = useState([]);   // campos que a busca preencheu
  const [monitorando, setMonitorando] = useState(false);
  const [autosEdit, setAutosEdit] = useState([]);       // Lote 119: autos do número, ao EDITAR um compromisso
  const regraNumRef = useRef("");
  const formRef = useRef(null);
  // Lote 57 (pedido do Robson): calendário clicável no campo Data — abre ao clicar/focar
  // no campo, fecha sozinho ao escolher um dia. O campo de texto com máscara continua
  // sendo o dono do valor (ver motivo em dateTimeMask.js) — o calendário só escreve nele,
  // não substitui.
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [form, setForm] = useState({
    event_type: "prazo",
    date: "",
    time: "",
    description: "",
    court: "",
    court_district: "",
    case_number: "",
    client_id: "none",
    meeting_url: "",
    meeting_id: "",
    meeting_password: "",
    parties: "",
    case_area: "outro",
    case_area_custom: "",
    party_author: "",
    party_defendant: "",
    hearing_phase: "",
    deadline_type: "",
    meeting_participants: "",
    meeting_subject: "",
    meeting_address: "",
    call_reminder_mode: "default",
    call_reminder_minutes_before: "",
    reminder: "",
    recurring_until: "",
    published_at: "",
  });
  const [saving, setSaving] = useState(false);
  // Lote 74 — "colar intimação/ata" na Descrição: a IA preenche o formulário e sugere prazos
  // extras (cada um vira um evento "Prazo" ao salvar, se continuar marcado).
  const [interpreting, setInterpreting] = useState(false);
  const [extras, setExtras] = useState([]); // [{id, checked, description, date, time, deadline_type}]
  // Lote 110 — prazo padrão: o evento "Prazo" novo já abre com a data da ciência (hoje) + 5 dias úteis,
  // contados a partir do dia seguinte, às 18:00. O botão "Prazo padrão" refaz a conta se a ciência mudar.
  const hojeBr = () => jsDateToDateBr(new Date());
  const [ciencia, setCiencia] = useState(hojeBr());
  const [padraoInfo, setPadraoInfo] = useState(null); // { date: "dd/mm/aaaa", text }
  const padraoAutoRef = useRef(false);
  const horaMexidaRef = useRef(false);
  const [sug, setSug] = useState({ date: false, time: false }); // campos preenchidos por sugestão (azul)
  const [showAllPresets, setShowAllPresets] = useState(false);
  // Lote 113 — anexar foto/PDF: o servidor devolve só o TEXTO (nada é guardado) e ele entra na Descrição.
  const [lendoArquivo, setLendoArquivo] = useState(false);
  const arquivoRef = useRef(null);
  const lerArquivo = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Arquivo maior que 10 MB", { description: "Reduza o arquivo ou envie só as páginas que interessam." });
      return;
    }
    setLendoArquivo(true);
    try {
      const fd = new FormData();
      fd.append("arquivo", file);
      const { data: r } = await http.post("/events/ler-arquivo", fd, { headers: { "Content-Type": undefined }, timeout: 180000 });
      const juntar = (atual) => (atual.trim() ? `${atual.trim()}\n\n${r.text}` : r.text);
      const depois = juntar(form.description);
      setForm((f) => ({ ...f, description: juntar(f.description) }));
      toast.success(r.origem === "ocr" ? "Foto lida — confira o texto" : "PDF lido", {
        description: r.origem === "ocr" ? "A leitura de imagem pode errar nomes, datas e números." : undefined,
      });
      if (r.aviso) toast.warning(r.aviso, { duration: 9000 });
      if (depois.trim().length >= 40) setAutoRun(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não consegui ler o arquivo", { duration: 9000 });
    } finally {
      setLendoArquivo(false);
      if (arquivoRef.current) arquivoRef.current.value = "";
    }
  };

  const aplicarPadrao = async ({ auto = false, cienciaBr = ciencia } = {}) => {
    const iso = dateBrToIso(cienciaBr);
    if (!iso) { if (!auto) toast.error("Informe a data da ciência (dd/mm/aaaa)"); return; }
    try {
      const d = await calcularPrazo({ dataIso: iso, dias: PRAZO_PADRAO_DIAS, base: "ciencia" });
      const novaData = isoToDateBr(d.data_final);
      setForm((f) => (auto && f.date ? f : {
        ...f, date: novaData, time: (auto || !horaMexidaRef.current) ? PRAZO_PADRAO_HORA : f.time,
      }));
      if (!(auto && form.date)) setSug({ date: true, time: auto || !horaMexidaRef.current });
      if (!(auto && form.date)) setPadraoInfo({ date: novaData, text: `${d.weekday_final}: ${PRAZO_PADRAO_DIAS} dias úteis a partir do dia seguinte à ciência, só feriados nacionais.` });
    } catch (e) {
      if (!auto) toast.error(e?.response?.data?.detail || "Não consegui calcular o prazo agora");
    }
  };
  const usarCalculo = (isoFinal, resumo) => {
    const novaData = isoToDateBr(isoFinal);
    setForm((f) => ({ ...f, date: novaData, time: horaMexidaRef.current ? f.time : PRAZO_PADRAO_HORA }));
    setSug({ date: true, time: !horaMexidaRef.current });
    setPadraoInfo({ date: novaData, text: resumo });
  };
  // Atalho de prazo pronto (mesmos da Calculadora de prazos): preenche o "Tipo de prazo" e calcula a data —
  // a partir da disponibilização quando preenchida; senão, da ciência (hoje).
  const aplicarPreset = async (pr) => {
    const dispIso = dateBrToIso(form.published_at);
    const baseIso = dispIso || dateBrToIso(ciencia);
    if (!baseIso) { toast.error("Informe a data da ciência ou da disponibilização"); return; }
    try {
      const d = await calcularPrazo({ dataIso: baseIso, dias: pr.dias, regime: "uteis", base: dispIso ? "disponibilizacao" : "ciencia" });
      const novaData = isoToDateBr(d.data_final);
      setForm((f) => ({ ...f, deadline_type: pr.label, date: novaData, time: horaMexidaRef.current ? f.time : PRAZO_PADRAO_HORA }));
      setSug({ date: true, time: !horaMexidaRef.current });
      setPadraoInfo({ date: novaData, text: `${d.weekday_final}: ${pr.label}, ${pr.dias} dias úteis (${pr.base_legal}). ${d.resumo}.` });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não consegui calcular o prazo agora");
    }
  };

  useEffect(() => {
    if (open) http.get("/clients").then((r) => setClients(r.data));
    if (open && !existing) {
      http.get("/processos").then((r) => setProcs(Array.isArray(r.data) ? r.data : [])).catch(() => setProcs([]));
    }
    if (!open) setBuscaProc(null);
  }, [open, existing]);

  // Regra de ouro (Lote 117): número completo (20 dígitos) -> consulta o que já existe e integra.
  formRef.current = form;
  const digitosProc = String(form.case_number || "").replace(/\D/g, "");
  const regraDigitos = digitosProc.length === 20 ? digitosProc : "";
  const buscarRegra = useCallback(async (dig, numero) => {
    try {
      const { data: r } = await http.get("/processo-por-numero", { params: { numero } });
      if (regraNumRef.current !== dig) return;
      setRegra(r);
      const m = r?.monitorado;
      const varios = (r?.monitorados || []).length > 1;     // Lote 118: vara/comarca só depois de escolher o autos
      if (m && !m.arquivado) {
        const f = formRef.current; const patch = {}; const feitos = [];
        if (!varios && !f.court && m.vara) { patch.court = m.vara; feitos.push("vara"); }
        if (!varios && !f.court_district && m.comarca) { patch.court_district = m.comarca; feitos.push("comarca"); }
        if (!f.party_author && m.party_author) { patch.party_author = m.party_author; feitos.push("partes"); }
        if (!f.party_defendant && m.party_defendant) { patch.party_defendant = m.party_defendant; if (!feitos.includes("partes")) feitos.push("partes"); }
        if ((!f.client_id || f.client_id === "none") && m.client_id) { patch.client_id = m.client_id; feitos.push("cliente"); }
        if (f.case_area === "outro" && !f.case_area_custom && KNOWN_CASE_AREAS.includes(m.case_area) && m.case_area !== "outro") { patch.case_area = m.case_area; feitos.push("tipo de ação"); }
        setRegraIntegrado(feitos);
        if (feitos.length) setForm((cur) => ({ ...cur, ...patch }));
      } else {
        setRegraIntegrado([]);
      }
    } catch (e) { if (regraNumRef.current === dig) setRegra(null); }
  }, []);
  useEffect(() => {
    if (!open || existing || !regraDigitos) { regraNumRef.current = ""; setRegra(null); setRegraIntegrado([]); return undefined; }
    if (regraNumRef.current === regraDigitos) return undefined;
    const t = setTimeout(() => { regraNumRef.current = regraDigitos; buscarRegra(regraDigitos, form.case_number); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing, regraDigitos]);
  const casoEdicao = String(form.case_number || "");
  useEffect(() => {
    if (!open || !existing || regraDigitos.length !== 20) { setAutosEdit([]); return undefined; }
    let vivo = true;
    http.get("/processo-por-numero", { params: { numero: casoEdicao } })
      .then((r) => { if (vivo) setAutosEdit((r.data?.monitorados || []).length > 1 ? r.data.monitorados : []); })
      .catch(() => { if (vivo) setAutosEdit([]); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing, regraDigitos]);
  const monitorarAgora = async () => {
    setMonitorando(true);
    try {
      await http.post("/processos", {
        numero: form.case_number.trim(),
        client_id: form.client_id === "none" ? null : form.client_id,
        comarca: form.court_district.trim() || null, vara: form.court.trim() || null,
        case_area: form.case_area === "outro" && form.case_area_custom.trim() ? form.case_area_custom.trim() : form.case_area,
        party_author: form.party_author.trim() || null, party_defendant: form.party_defendant.trim() || null,
      });
      invalidarProcessosMapa();
      toast.success("Processo cadastrado e monitorado");
      regraNumRef.current = regraDigitos;
      await buscarRegra(regraDigitos, form.case_number);
      setProcs((await http.get("/processos")).data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Não consegui cadastrar o processo agora."); } finally { setMonitorando(false); }
  };
  const abrirDaRegra = (to) => { setOpen(false); navigate(to); };

  useEffect(() => {
    if (open && existing) {
      const h = existing.hearing || {};
      // Compatibilidade: eventos antigos só têm a string livre "Fulano x Sicrano" — tenta
      // separar em autor/réu pra preencher os dois campos novos (best-effort).
      let author = h.party_author || "";
      let defendant = h.party_defendant || "";
      if (!author && !defendant && h.parties && h.parties.includes(" x ")) {
        const [a, d] = h.parties.split(" x ");
        author = a?.trim() || "";
        defendant = d?.trim() || "";
      }
      // "Tipo de ação" com texto livre (Lote novo, 29/09/2026): um evento antigo pode ter
      // case_area vindo como um texto qualquer digitado antes (não só as 4 opções fixas).
      // Nesse caso, mostramos o botão "Outro" selecionado e pré-preenchemos o campo de
      // texto livre com o valor real, em vez de deixar sem nenhum botão marcado.
      const rawCaseArea = h.case_area || "outro";
      const isKnownCaseArea = KNOWN_CASE_AREAS.includes(rawCaseArea);
      setForm({
        event_type: h.event_type || "audiencia",
        date: isoToDateBr(h.date),
        time: h.time || "",
        description: brDatesInText(h.description || existing.raw_text || ""),
        court: h.court || "",
        court_district: h.court_district || "",
        case_number: h.case_number || "",
        autos_processo_id: h.autos_processo_id || "",
        client_id: existing.client_id || "none",
        meeting_url: h.meeting_url || "",
        meeting_id: h.meeting_id || "",
        meeting_password: h.meeting_password || "",
        parties: h.parties || "",
        case_area: isKnownCaseArea ? rawCaseArea : "outro",
        case_area_custom: isKnownCaseArea ? "" : (rawCaseArea === "outro" ? "" : rawCaseArea),
        party_author: author,
        party_defendant: defendant,
        hearing_phase: h.hearing_phase || "",
        deadline_type: h.deadline_type || "",
        meeting_participants: h.meeting_participants || h.parties || "",
        meeting_subject: h.meeting_subject || "",
        meeting_address: h.meeting_address || "",
        call_reminder_mode: h.call_reminder_mode || "default",
        call_reminder_minutes_before: h.call_reminder_minutes_before ?? "",
        reminder: reminderFromHearing(h),
        recurring_until: h.recurring_until ? isoToDateBr(h.recurring_until) : "",
        published_at: (h.published_at || (existing.source !== "manual" ? existing.published_at : "")) ?
          isoToDateBr(h.published_at || existing.published_at) : "",
      });
      setExtras([]);
      setSug({ date: false, time: false });
    } else if (open && !existing) {
      // Calendário estilo Apple (Lote novo, 29/09/2026): clicar num horário vazio na
      // visão Dia/Semana abre este diálogo já com a data/hora daquele horário, em vez de
      // sempre cair na hora atual — initialDate/initialTime vêm do CalendarPage.
      const pf = prefill || {};
      setForm({
        event_type: pf.event_type || "prazo",
        date: initialDate ? isoToDateBr(initialDate) : "",
        time: initialTime || nowTime(),
        description: pf.description || "",
        court: pf.court || "", court_district: pf.court_district || "",
        case_number: pf.case_number ? maskCaseNumberInput(pf.case_number) : "", client_id: pf.client_id || "none",
        autos_processo_id: pf.autos_processo_id || "",
        meeting_url: "", meeting_id: "", meeting_password: "", parties: "",
        case_area: pf.case_area || inferCaseArea(pf.case_number) || "outro", case_area_custom: "",
        party_author: pf.party_author || "", party_defendant: pf.party_defendant || "", hearing_phase: "",
        deadline_type: "", meeting_participants: "", meeting_subject: "", meeting_address: "",
        call_reminder_mode: (pf.event_type || "prazo") === "prazo" ? "off" : "default", call_reminder_minutes_before: "",
        reminder: "", recurring_until: "", published_at: pf.published_at ? isoToDateBr(pf.published_at) : "",
      });
      setExtras([]);
      // "Agendar prazo" a partir de uma publicação: a IA já lê o texto e sugere os prazos.
      setAutoRun(Boolean(pf.autoInterpret));
      setCiencia(hojeBr());
      setPadraoInfo(null);
      setSug({ date: false, time: false });
      setShowAllPresets(false);
      horaMexidaRef.current = false;
    }
  }, [open, existing, initialDate, initialTime, prefill]);

  // Lote 110 — evento "Prazo" novo sem data: já sugere a data do prazo padrão (uma vez por abertura).
  useEffect(() => {
    if (!open) { padraoAutoRef.current = false; return; }
    if (isEdit) return;
    if (form.event_type === "prazo" && !form.date && !padraoAutoRef.current) {
      padraoAutoRef.current = true;
      aplicarPadrao({ auto: true, cienciaBr: hojeBr() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, form.event_type, form.date]);

  useEffect(() => {
    if (autoRun && open && form.description.trim().length >= 40) {
      setAutoRun(false);
      interpretText();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun, open, form.description]);

  const interpretText = async () => {
    setInterpreting(true);
    try {
      const { data: d } = await http.post("/events/interpretar", { text: form.description });
      const f = d.fields || {};
      const prazos = d.prazos || [];
      const hasMain = Boolean(f.date);
      // Lote 113: só o que a IA achou entra no formulário (atualização funcional) — antes, o formulário
      // inteiro de ANTES da leitura era regravado e apagava o prazo padrão calculado nesse meio tempo.
      const next = {};
      if (hasMain) {
        next.event_type = d.mentions_hearing ? "audiencia" : form.event_type;
        next.date = isoToDateBr(f.date);
        if (f.time) next.time = f.time;
      } else if (prazos.length > 0) {
        // Sem audiência no texto: o 1º prazo vira o evento principal, os demais ficam como extras.
        next.event_type = "prazo";
        next.date = isoToDateBr(prazos[0].date);
        next.time = d.default_time || "18:00";
        next.deadline_type = prazos[0].deadline_type || "";
      }
      if (next.event_type === "prazo" && form.event_type !== "prazo" && form.call_reminder_mode === "default") {
        next.call_reminder_mode = "off";
      }
      if (f.court) next.court = f.court;
      if (f.court_district) next.court_district = f.court_district;
      if (f.case_number) next.case_number = maskCaseNumberInput(f.case_number);
      if (f.case_area && KNOWN_CASE_AREAS.includes(f.case_area)) {
        next.case_area = f.case_area;
        next.case_area_custom = "";
      }
      if (f.party_author) next.party_author = f.party_author;
      if (f.party_defendant) next.party_defendant = f.party_defendant;
      if (f.hearing_phase && next.event_type === "audiencia") next.hearing_phase = f.hearing_phase;
      if (hasMain || prazos.length > 0) setSug({ date: true, time: Boolean(f.time) || (!hasMain && prazos.length > 0) });
      // A disponibilização que já veio da publicação (DJEN) manda; a data achada no texto só preenche se estiver vazia.
      setForm((cur) => ({ ...cur, ...next, ...(d.published_at && !cur.published_at ? { published_at: isoToDateBr(d.published_at) } : {}) }));
      if (d.published_at) setSug((x) => ({ ...x, pub: x.pub || !form.published_at })); // achada no texto = sugestão (azul)
      const rest = hasMain ? prazos : prazos.slice(1);
      setExtras(rest.map((p, i) => ({
        id: `x${i}`, checked: true, description: p.description, date: isoToDateBr(p.date),
        time: d.default_time || "18:00", deadline_type: p.deadline_type || "",
      })));
      const found = (hasMain ? 1 : 0) + prazos.length;
      if (found === 0) {
        toast.info("A IA não achou data nem prazo nesse texto", {
          description: "Preencha os campos manualmente ou cole o texto completo.",
        });
      } else {
        toast.success("Texto interpretado — confira antes de salvar", {
          description: rest.length > 0
            ? `${rest.length} prazo(s) sugerido(s) abaixo; desmarque os que não quiser.`
            : undefined,
        });
      }
      if ((d.skipped || []).length > 0) {
        toast.warning(`${d.skipped.length} prazo(s) sem data calculável`, {
          description: "Cadastre à mão: " + d.skipped.map((x) => x.description).join("; "),
          duration: 10000,
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível interpretar o texto");
    } finally {
      setInterpreting(false);
    }
  };

  const submit = async () => {
    // Lote 29: data/hora agora são campos de texto com máscara (não mais o
    // <input type="date">/"time"> nativo — ver frontend/src/lib/dateTimeMask.js pro
    // motivo). form.date/form.time guardam o texto digitado ("dd/mm/aaaa" e "hh:mm");
    // convertemos pro formato do backend só aqui, na hora de salvar.
    const date = dateBrToIso(form.date);
    const time = isValidTime(form.time) ? form.time : "";
    // Descrição deixou de ser obrigatória (pedido do Robson, 29/09/2026) — só data e hora
    // travam o salvamento agora. A mensagem continua nomeando exatamente o que falta.
    const missing = [];
    if (!date) missing.push(form.date ? "data (use o formato dd/mm/aaaa)" : "data");
    if (!time) missing.push(form.time ? "hora (use o formato hh:mm)" : "hora");
    if (missing.length > 0) {
      toast.error(`Preencha: ${missing.join(", ")}`, { duration: 6000 });
      return;
    }
    setSaving(true);
    try {
      // "Tipo de ação" com texto livre (Lote novo, 29/09/2026): quando o usuário escolhe
      // "Outro" e digita uma área específica (ex: "Tributário"), é esse texto que vira o
      // case_area de verdade — o botão "Outro" é só o gatilho pra mostrar o campo.
      const resolvedCaseArea =
        form.case_area === "outro" && form.case_area_custom.trim()
          ? form.case_area_custom.trim()
          : form.case_area || null;
      const payload = {
        event_type: form.event_type,
        date,
        time,
        description: form.description,
        court: form.court || null,
        court_district: form.court_district || null,
        case_number: form.case_number || null,
        autos_processo_id: form.autos_processo_id || null,
        client_id: form.client_id === "none" ? null : form.client_id,
        meeting_url: form.meeting_url || null,
        meeting_id: form.meeting_id || null,
        meeting_password: form.meeting_password || null,
        case_area: resolvedCaseArea,
        party_author: form.event_type === "reuniao" ? null : (form.party_author || null),
        party_defendant: form.event_type === "reuniao" ? null : (form.party_defendant || null),
        hearing_phase: form.event_type === "audiencia" ? (form.hearing_phase || null) : null,
        deadline_type: form.event_type === "prazo" ? (form.deadline_type || null) : null,
        meeting_participants: form.event_type === "reuniao" ? (form.meeting_participants || null) : null,
        meeting_subject: form.event_type === "reuniao" ? (form.meeting_subject || null) : null,
        meeting_address: form.event_type === "reuniao" ? (form.meeting_address || null) : null,
        call_reminder_mode: form.call_reminder_mode || "default",
        call_reminder_minutes_before:
          form.call_reminder_mode === "on" && form.call_reminder_minutes_before
            ? Math.min(60, Math.max(5, parseInt(form.call_reminder_minutes_before)))
            : null,
        recurring_daily: form.reminder === "daily",
        recurring_until:
          form.reminder === "daily" && form.recurring_until
            ? dateBrToIso(form.recurring_until) || null
            : null,
        reminder_minutes_before:
          form.reminder !== "" && form.reminder !== "daily" ? parseInt(form.reminder, 10) : null,
        published_at: form.event_type === "reuniao" ? null : (form.published_at ? dateBrToIso(form.published_at) || null : null),
      };
      let r;
      if (isEdit) {
        r = await http.put(`/events/manual/${existing.pub_id}`, payload);
        toast.success("Evento atualizado");
      } else {
        r = await http.post("/events/manual", payload);
        // Prazos sugeridos pela IA (Lote 74): cada marcado vira um evento "Prazo" do mesmo processo.
        const chosen = extras.filter((x) => x.checked && dateBrToIso(x.date) && isValidTime(x.time));
        let extraFail = 0;
        for (const x of chosen) {
          try {
            await http.post("/events/manual", {
              event_type: "prazo", date: dateBrToIso(x.date), time: x.time,
              description: x.description, deadline_type: x.deadline_type || null,
              court: payload.court, court_district: payload.court_district,
              case_number: payload.case_number, client_id: payload.client_id,
              case_area: payload.case_area, party_author: payload.party_author,
              party_defendant: payload.party_defendant, call_reminder_mode: "off",
              published_at: payload.published_at,
            });
          } catch (err) { extraFail += 1; }
        }
        if (chosen.length > 0) {
          if (extraFail) toast.error(`${extraFail} prazo(s) não foram salvos — confira a agenda`);
          else toast.success(`${chosen.length} prazo(s) sugerido(s) também agendado(s)`);
        }
        toast.success("Evento cadastrado", {
          description: r.data.calendar_synced
            ? "Já sincronizado com o Google Calendar"
            : "Salvo — conecte o Google Calendar em Configurações para sincronizar automaticamente",
        });
      }
      // Lote 98: prazo digitado em sábado/domingo/feriado é prorrogado pelo servidor — avisa.
      const hz = r.data?.hearing;
      if (hz?.event_type === "prazo" && hz.prazo_prorrogado_de && hz.date && hz.date !== date) {
        toast.info("Prazo prorrogado para o próximo dia útil", {
          description: `A data caía em dia sem expediente; ficou em ${hz.date.split("-").reverse().join("/")}.`,
          duration: 9000,
        });
      }
      // Lote 34.2b: alerta não-bloqueante de conflito de horário — o evento já foi salvo
      // normalmente, isso é só um aviso pra conferir a agenda, não impede nada.
      const conflicts = r.data?.conflicts || [];
      if (conflicts.length > 0) {
        const other = conflicts[0];
        toast.warning("Conflito de horário", {
          description: `Esse horário está perto de ${other.label.toLowerCase()} às ${other.time}${
            conflicts.length > 1 ? ` (e mais ${conflicts.length - 1})` : ""
          } — confira a agenda.`,
          duration: 10000,
          action: {
            label: "Ver conflito",
            onClick: () => navigate(`/calendario?data=${r.data?.hearing?.date || ""}&conflito=${[r.data?.pub_id, ...conflicts.map((c) => c.pub_id)].filter(Boolean).join(",")}`),
          },
        });
      }
      setOpen(false);
      onCreated?.(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Erro ao salvar", {
        description: detail
          ? undefined
          : `HTTP ${e?.response?.status ?? "sem resposta"} — ${e?.message || "erro desconhecido"}`,
        duration: 12000,
      });
    } finally {
      setSaving(false);
    }
  };

  // Lote 116: processos monitorados para o atalho — prioritários primeiro, depois os mais recentes; arquivados ficam fora.
  const procsAtivos = procs.filter((p) => !p.arquivado && p.numero);
  const procsOrdenados = [...procsAtivos.filter((p) => p.prioritario), ...procsAtivos.filter((p) => !p.prioritario)];
  const procRotulo = (p) => {
    const cli = clients.find((c) => c.client_id === p.client_id)?.name;
    return cli || p.apelido || [p.party_author, p.party_defendant].filter(Boolean).join(" × ") || p.numero;
  };
  const procFinal = (p) => `…${String(p.numero || "").slice(-9)}`;
  const mesmoNumero = (p) => !!form.case_number && form.case_number.replace(/\D/g, "") === String(p.numero || "").replace(/\D/g, "");
  // Lote 118: vários autos com o mesmo número → só o autos escolhido fica marcado
  const irmaosDe = (p) => procsAtivos.filter((x) => String(x.numero || "").replace(/\D/g, "") === String(p.numero || "").replace(/\D/g, ""));
  const procSelecionado = (p) => mesmoNumero(p) && (irmaosDe(p).length < 2 || form.autos_processo_id === p.processo_id);
  const escolherProcesso = (p) => {
    const area = KNOWN_CASE_AREAS.includes(p.case_area) ? p.case_area : (inferCaseArea(p.numero) || null);
    setForm((f) => ({
      ...f,
      case_number: maskCaseNumberInput(p.numero),
      autos_processo_id: irmaosDe(p).length > 1 ? p.processo_id : "",
      court: p.vara || f.court,
      court_district: p.comarca || f.court_district,
      client_id: p.client_id || f.client_id,
      party_author: p.party_author || f.party_author,
      party_defendant: p.party_defendant || f.party_defendant,
      case_area: area || f.case_area,
      case_area_custom: area ? "" : f.case_area_custom,
    }));
    setBuscaProc(null);
  };
  const buscaNorm = (t) => String(t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const procsFiltrados = buscaProc === null ? [] : procsOrdenados.filter((p) => {
    const q = buscaNorm(buscaProc).trim();
    if (!q) return true;
    const alvo = buscaNorm([procRotulo(p), p.apelido, p.numero, p.party_author, p.party_defendant, p.vara, p.comarca].filter(Boolean).join(" "));
    return alvo.includes(q) || String(p.numero || "").replace(/\D/g, "").includes(q.replace(/\D/g, "") || "§");
  });

  const typeLabel = TYPES.find((t) => t.value === form.event_type)?.label || "Evento";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
{!hideTrigger && (
            <DialogTrigger asChild>
        {trigger || (
          <Button
            data-testid="new-event-button"
            variant="outline"
            className="gap-2 border-stone-300 hover:bg-stone-100"
          >
            <Plus className="w-4 h-4" strokeWidth={2} /> Novo evento
          </Button>
        )}
      </DialogTrigger>
      )}
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {isEdit ? `Editar ${typeLabel.toLowerCase()}` : "Novo evento manual"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Ajuste os dados e regenere o link do Google Calendar e os rascunhos."
              : `Cadastre ${form.event_type === "audiencia" ? "uma audiência" : `um ${typeLabel.toLowerCase()}`} sem depender do diário. Entra direto na sua agenda.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs text-stone-500 mb-1.5 block">Tipo</Label>
            <div className="grid grid-cols-3 gap-2">
              {TYPES.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  data-testid={`event-type-${value}`}
                  type="button"
                  onClick={() => {
                    // Lembrete por ligação desativado por padrão pra prazo (Lote 56, pedido
                    // do Robson, 30/09/2026) — ligar pra avisar de um prazo não costuma
                    // fazer sentido do mesmo jeito que pra audiência/reunião. Só mexe
                    // automaticamente se o campo ainda estiver no "Padrão" (não sobrescreve
                    // uma escolha que o usuário já fez na mão).
                    let nextMode = form.call_reminder_mode;
                    if (value === "prazo" && form.call_reminder_mode === "default") {
                      nextMode = "off";
                    } else if (form.event_type === "prazo" && value !== "prazo" && form.call_reminder_mode === "off") {
                      nextMode = "default";
                    }
                    setForm({ ...form, event_type: value, call_reminder_mode: nextMode });
                  }}
                  className={`px-3 py-2.5 rounded-md border text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
                    form.event_type === value
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" strokeWidth={2} /> {label}
                  {value === "reuniao" && <ChevronDown className={`w-3.5 h-3.5 transition-transform ${form.event_type === "reuniao" ? "rotate-180" : ""}`} strokeWidth={2} />}
                </button>
              ))}
            </div>
            {form.event_type === "reuniao" && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="compromisso-tipos">
                <span className="text-[11px] text-stone-500">Tipo:</span>
                {COMPROMISSO_TIPOS.map((t) => (
                  <button
                    key={t} type="button" data-testid={`compromisso-tipo-${t.toLowerCase()}`}
                    onClick={() => setForm({ ...form, meeting_subject: t })}
                    className={`px-3 h-8 rounded-full border text-xs font-semibold ${
                      form.meeting_subject === t ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"
                    }`}
                  >
                    {t}
                  </button>
                ))}
                <button
                  type="button" data-testid="compromisso-tipo-outro"
                  onClick={() => { setForm({ ...form, meeting_subject: "" }); setTimeout(() => document.querySelector("[data-testid=event-meeting-subject-input]")?.focus(), 50); }}
                  className="px-3 h-8 rounded-full border border-stone-200 bg-white text-xs font-semibold text-stone-700 hover:border-stone-300"
                >
                  Outro…
                </button>
              </div>
            )}
          </div>

          {!isEdit && procsOrdenados.length > 0 && (
            <div data-testid="event-procs-atalho">
            <Label className="text-xs text-stone-500 mb-1.5 block">Processo monitorado (atalho)</Label>
              <div className="flex flex-wrap items-center gap-1.5">
                {procsOrdenados.slice(0, 3).map((p) => {
                  const sel = procSelecionado(p);
                  return (
                    <button
                      key={p.processo_id}
                      type="button"
                      data-testid={`event-proc-${p.processo_id}`}
                      aria-pressed={sel}
                      onClick={() => escolherProcesso(p)}
                      title={p.numero}
                      className={`inline-flex items-center gap-1.5 max-w-full min-h-[36px] px-3 rounded-full border text-xs font-medium ${sel ? SEL_CINZA : "bg-white border-stone-300 text-stone-700 hover:border-stone-500"}`}
                    >
                      {p.prioritario ? <Star className="w-3 h-3 fill-gold-500 text-gold-500 shrink-0" strokeWidth={1.75} /> : null}
                      <span className="truncate max-w-[9.5rem]">{procRotulo(p)}</span>
                      {(p.autos_total > 1 || irmaosDe(p).length > 1) && <AutosTag autos={p.autos} nome={p.autos_nome} className="shrink-0" />}
                      <span className="text-stone-400 tabular-nums shrink-0">{procFinal(p)}</span>
                    </button>
                  );
                })}
                {procsOrdenados.length > 3 && (
                  <button
                    type="button"
                    data-testid="event-procs-buscar"
                    aria-expanded={buscaProc !== null}
                    onClick={() => setBuscaProc((b) => (b === null ? "" : null))}
                    className="min-h-[36px] px-2 text-xs font-semibold text-gold-800 underline underline-offset-2 hover:text-gold-900"
                  >
                    Buscar ›
                  </button>
                )}
              </div>
              {buscaProc !== null && (
                <div className="mt-2 rounded-md border border-stone-200 bg-white p-2" data-testid="event-procs-painel">
                  <Input
                    data-testid="event-procs-busca"
                    autoFocus
                    value={buscaProc}
                    onChange={(e) => setBuscaProc(e.target.value)}
                    placeholder="Buscar por cliente, apelido, parte ou número"
                    className="h-9 mb-1.5"
                  />
                  <ul className="max-h-48 overflow-y-auto" data-testid="event-procs-lista">
                    {procsFiltrados.length === 0 ? (
                      <li className="px-2 py-2 text-xs text-stone-500">Nenhum processo encontrado.</li>
                    ) : procsFiltrados.map((p) => (
                      <li key={p.processo_id}>
                        <button
                          type="button"
                          data-testid={`event-procs-item-${p.processo_id}`}
                          onClick={() => escolherProcesso(p)}
                          className="w-full text-left px-2 py-2 min-h-[44px] rounded hover:bg-stone-50"
                        >
                          <span className="flex items-center gap-1.5 text-sm font-medium text-stone-800">
                            {p.prioritario ? <Star className="w-3 h-3 fill-gold-500 text-gold-500 shrink-0" strokeWidth={1.75} /> : null}
                            <span className="truncate">{procRotulo(p)}</span>
                            {irmaosDe(p).length > 1 && <AutosTag autos={p.autos} nome={p.autos_nome} className="shrink-0" />}
                          </span>
                          <span className="block text-[11px] text-stone-500 tabular-nums truncate">{p.numero}{p.vara ? ` · ${p.vara}` : ""}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-stone-500 mb-1.5 block">Data</Label>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                {/* Correção do campo Data travado (05/10/2026): o Input era um PopoverTrigger, e
                    o Radix tira o foco do campo ao abrir o calendário (e o onFocus reabria o
                    calendário em loop) — o cursor nunca ficava no campo e não dava pra digitar.
                    Agora o Input é só a "âncora" de posição do calendário, o foco continua nele
                    (onOpenAutoFocus bloqueado) e clicar no próprio campo não conta como "clique
                    fora" que fecha o calendário. */}
                <PopoverAnchor asChild>
                  <Input
                    data-testid="event-date-input"
                    type="text"
                    inputMode="numeric"
                    placeholder="dd/mm/aaaa"
                    maxLength={10}
                    value={form.date}
                    className={sug.date ? SUG_FIELD : ""}
                    onChange={(e) => { setSug((x) => ({ ...x, date: false })); setForm({ ...form, date: maskDateInput(e.target.value) }); }}
                    onClick={() => setDatePickerOpen(true)}
                    onFocus={() => setDatePickerOpen(true)}
                  />
                </PopoverAnchor>
                <PopoverContent
                  className="w-auto p-0"
                  align="start"
                  data-testid="event-date-calendar"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  onInteractOutside={(e) => {
                    if (e.target instanceof Element && e.target.closest('[data-testid="event-date-input"]')) {
                      e.preventDefault();
                    }
                  }}
                >
                  <CalendarPicker
                    mode="single"
                    locale={ptBR}
                    selected={dateBrToJsDate(form.date)}
                    defaultMonth={dateBrToJsDate(form.date) || new Date()}
                    onSelect={(d) => {
                      if (!d) return;
                      setSug((x) => ({ ...x, date: false }));
                      setForm({ ...form, date: jsDateToDateBr(d) });
                      setDatePickerOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs text-stone-500 mb-1.5 block">Hora</Label>
              <Input
                data-testid="event-time-input"
                type="text"
                inputMode="numeric"
                placeholder="hh:mm"
                maxLength={5}
                value={form.time}
                className={sug.time ? SUG_FIELD : ""}
                onChange={(e) => { horaMexidaRef.current = true; setSug((x) => ({ ...x, time: false })); setForm({ ...form, time: maskTimeInput(e.target.value) }); }}
              />
            </div>
          </div>
          {form.event_type === "prazo" && padraoInfo && padraoInfo.date === form.date && (
            <p className="-mt-2 text-[11px] text-[#0b4f94]" data-testid="prazo-padrao-info">
              Vence {padraoInfo.text}
            </p>
          )}

          <div>
            <div className="flex flex-col gap-0.5 mb-1.5">
              <Label className="text-xs text-stone-500">Descrição</Label>
              <span className="text-[11px] text-stone-400" data-testid="event-description-hint">
                Descreva o compromisso ou cole a intimação/ata (“IA” sugere o agendamento)
              </span>
            </div>
            <Textarea
              data-testid="event-description-input"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Descreva o compromisso ou cole a intimação/ata…"
            />
            {!isEdit && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  ref={arquivoRef} type="file" accept="application/pdf,image/*" className="hidden"
                  data-testid="event-attach-input" onChange={(e) => lerArquivo(e.target.files?.[0])}
                />
                <Button
                  type="button" variant="outline" size="sm" data-testid="event-attach-button"
                  onClick={() => arquivoRef.current?.click()} disabled={lendoArquivo || interpreting}
                  title="Foto da intimação/ata ou PDF — o arquivo não é guardado, só o texto é usado"
                  className="gap-2 border-stone-300 text-stone-700"
                >
                  {lendoArquivo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" strokeWidth={1.75} />}
                  {lendoArquivo ? "Lendo o arquivo…" : "Anexar foto/PDF"}
                </Button>
                {form.description.trim().length >= 40 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="event-interpret-button"
                    onClick={interpretText}
                    disabled={interpreting || lendoArquivo}
                    className="gap-2 border-gold-500 text-gold-800 hover:bg-gold-100"
                  >
                    {interpreting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />}
                    {interpreting ? "Interpretando com IA…" : "Interpretar com IA e preencher"}
                  </Button>
                )}
              </div>
            )}
          </div>

          {form.event_type !== "reuniao" && (
            <div>
              <Label className="text-xs text-stone-500 mb-1.5 block">Data da disponibilização (opcional)</Label>
              <Input
                data-testid="event-published-at-input"
                placeholder="dd/mm/aaaa"
                inputMode="numeric"
                maxLength={10}
                value={form.published_at}
                onChange={(e) => { setSug((x) => ({ ...x, pub: false })); setForm({ ...form, published_at: maskDateInput(e.target.value) }); }}
                className={`w-40 ${sug.pub ? SUG_FIELD : ""}`}
              />
              <Dica className="text-[11px] text-stone-400 mt-1">{`Dia em que a intimação foi disponibilizada no diário. Vem sozinha de uma publicação ou do texto colado/anexado (em azul, para você conferir) e fica visível no evento.`}</Dica>
            </div>
          )}

          {extras.length > 0 && (
            <div className="rounded-md border border-[#8cc7f5] bg-[#f3f9ff] p-3 space-y-2" data-testid="event-extras-box">
              <p className="text-xs font-semibold text-[#0b4f94]">Prazos sugeridos pela IA — marque os que quer agendar</p>
              {extras.map((x) => (
                <div key={x.id} className="flex items-start gap-2 text-sm" data-testid={`event-extra-${x.id}`}>
                  <input
                    type="checkbox"
                    checked={x.checked}
                    onChange={(e) => setExtras(extras.map((y) => (y.id === x.id ? { ...y, checked: e.target.checked } : y)))}
                    className="mt-2.5 h-4 w-4 accent-stone-900"
                    aria-label="Agendar este prazo"
                  />
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <Input
                      value={x.description}
                      onChange={(e) => setExtras(extras.map((y) => (y.id === x.id ? { ...y, description: e.target.value } : y)))}
                      className="h-9"
                    />
                    <div className="flex gap-2">
                      <Input
                        value={x.date}
                        placeholder="dd/mm/aaaa"
                        inputMode="numeric"
                        maxLength={10}
                        onChange={(e) => setExtras(extras.map((y) => (y.id === x.id ? { ...y, date: maskDateInput(e.target.value) } : y)))}
                        className="h-9 w-32 !border-[#8cc7f5] bg-white"
                      />
                      <Input
                        value={x.time}
                        placeholder="hh:mm"
                        inputMode="numeric"
                        maxLength={5}
                        onChange={(e) => setExtras(extras.map((y) => (y.id === x.id ? { ...y, time: maskTimeInput(e.target.value) } : y)))}
                        className="h-9 w-20 !border-[#8cc7f5] bg-white"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div>
            <Label className="text-xs text-stone-500 mb-1.5 block">Tipo de ação</Label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {CASE_AREAS.map(({ value, label }) => (
                <button
                  key={value}
                  data-testid={`case-area-${value}`}
                  type="button"
                  onClick={() => setForm({ ...form, case_area: value })}
                  className={`px-2 py-2 rounded-md border text-xs font-medium transition-colors ${
                    form.case_area === value
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {form.case_area === "outro" && (
              <Input
                data-testid="case-area-custom-input"
                value={form.case_area_custom}
                onChange={(e) => setForm({ ...form, case_area_custom: e.target.value })}
                placeholder="Especifique a área (ex: Tributário, Consumidor, Família…)"
                className="mt-2"
              />
            )}
            {form.event_type === "prazo" && PRESETS[form.case_area] && (() => {
              const lista = PRESETS[form.case_area];
              const vistos = showAllPresets ? lista : lista.slice(0, 6);
              return (
                <div className="mt-2" data-testid="prazo-presets">
                  <p className="text-[11px] text-stone-500 mb-1.5">
                    Prazos prontos — contam a partir da {dateBrToIso(form.published_at) ? "disponibilização" : "ciência (hoje)"}:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {vistos.map((pr) => (
                      <button
                        key={pr.key} type="button" data-testid={`prazo-preset-${pr.key}`} title={pr.base_legal}
                        onClick={() => aplicarPreset(pr)}
                        className={`px-2.5 h-8 rounded-full border text-[11px] font-semibold ${
                          form.deadline_type === pr.label
                            ? "bg-[#0b4f94] border-[#0b4f94] text-white"
                            : "bg-[#f3f9ff] border-[#8cc7f5] text-[#0b4f94] hover:border-[#0b4f94]"
                        }`}
                      >
                        {pr.label.replace(/ \(.*\)$/, "")} · {pr.dias}
                      </button>
                    ))}
                    {lista.length > 6 && (
                      <button
                        type="button" data-testid="prazo-presets-mais" onClick={() => setShowAllPresets((v) => !v)}
                        className="px-2.5 h-8 rounded-full border border-stone-200 bg-white text-[11px] font-semibold text-stone-600"
                      >
                        {showAllPresets ? "menos" : `mais ${lista.length - 6}`}
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {form.event_type === "audiencia" && (
            <div>
              <Label className="text-xs text-stone-500 mb-1.5 block">Fase da audiência</Label>
              <div className="grid grid-cols-4 gap-2">
                {PHASES.map(({ value, label }) => (
                  <button
                    key={value}
                    data-testid={`hearing-phase-${value}`}
                    type="button"
                    onClick={() => setForm({ ...form, hearing_phase: value })}
                    className={`px-2 py-2 rounded-md border text-xs font-medium transition-colors ${
                      form.hearing_phase === value
                        ? "bg-stone-900 text-white border-stone-900"
                        : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {form.event_type === "prazo" && (
            <div>
              <Label className="text-xs text-stone-500 mb-1.5 block">Tipo de prazo</Label>
              <Input
                data-testid="event-deadline-type-input"
                value={form.deadline_type}
                onChange={(e) => setForm({ ...form, deadline_type: e.target.value })}
                placeholder="Ex: Contestação, Recurso Ordinário, Réplica…"
              />
              <div className="mt-3 rounded-md border border-stone-200 bg-stone-50 p-3 space-y-3" data-testid="prazo-padrao-box">
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <Label className="text-xs text-stone-500 mb-1.5 block">Ciência</Label>
                    <Input
                      data-testid="prazo-ciencia-input" inputMode="numeric" placeholder="dd/mm/aaaa" maxLength={10}
                      value={ciencia} onChange={(e) => setCiencia(maskDateInput(e.target.value))} className="h-9 w-32"
                    />
                  </div>
                  <Button
                    type="button" variant="outline" size="sm" data-testid="prazo-padrao-button"
                    onClick={() => aplicarPadrao()} className="h-9 border-stone-300"
                  >
                    Prazo padrão
                  </Button>
                  <span className="text-[11px] text-stone-500 pb-2">{PRAZO_PADRAO_DIAS} dias úteis, a partir do dia seguinte</span>
                </div>
                <PrazoCalculadora
                  description={form.description} publishedAt={form.published_at} cienciaBr={ciencia} onUse={usarCalculo}
                />
              </div>
            </div>
          )}

          {form.event_type === "reuniao" && (
            <div>
              {/* Era "Assunto" — virou "Tipo" (Lote 56, pedido do Robson, 30/09/2026), já
                  que o botão de tipo agora é "Outro" (era "Reunião"): esse campo (mesmo
                  meeting_subject por trás) é onde se especifica o que é, ex. Perícia,
                  Diligência, Reunião. Mesmo campo de antes, só relabelado — ele já
                  aparecia como sub-etiqueta em toda lista de eventos, então reaproveitar
                  evita perder esse comportamento com um campo novo do zero. */}
              <Label className="text-xs text-stone-500 mb-1.5 block">Tipo</Label>
              <Input
                data-testid="event-meeting-subject-input"
                value={form.meeting_subject}
                onChange={(e) => setForm({ ...form, meeting_subject: e.target.value })}
                placeholder="Ex: Perícia, Reunião, Diligência…"
              />
            </div>
          )}

          {form.event_type === "reuniao" ? (
            <div>
              <Label className="text-xs text-stone-500 mb-1.5 block">Participantes</Label>
              <Input
                data-testid="event-meeting-participants-input"
                value={form.meeting_participants}
                onChange={(e) => setForm({ ...form, meeting_participants: e.target.value })}
                placeholder="Ex: Cliente Fulano, Dr. Sicrano, perito"
              />
              <Dica className="text-[11px] text-stone-400 mt-1.5">{`Aparece no lugar da descrição na lista de próximos compromissos. Se deixar em branco e vincular um cliente abaixo, usamos o nome dele.`}</Dica>
            </div>
          ) : null}

          {form.event_type === "reuniao" && (
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <MapPin className="w-3.5 h-3.5 text-stone-500" strokeWidth={2} />
                <Label className="text-xs text-stone-500">Endereço</Label>
              </div>
              <Input
                data-testid="event-meeting-address-input"
                value={form.meeting_address}
                onChange={(e) => setForm({ ...form, meeting_address: e.target.value })}
                placeholder="Opcional — ex: Rua Exemplo, 123, sala 4"
              />
            </div>
          )}

          <div>
            <Label className="text-xs text-stone-500 mb-1.5 block">Processo</Label>
            <SugestaoInput
              testid="event-case-input"
              fonte="numeros"
              inputMode="numeric"
              fantasma="_______-__.____._.__.____"
              transformar={maskCaseNumberInput}
              value={form.case_number}
              onValueChange={(v) => setForm((f) => ({ ...f, case_number: v }))}
              placeholder="_______-__.____._.__.____"
            />
            {isEdit && autosEdit.length > 1 && (
              <div className="mt-2" data-testid="event-edit-autos">
                <Label className="text-xs text-stone-500 mb-1.5 block">De qual autos é este compromisso?</Label>
                <div className="flex flex-wrap gap-1.5">
                  {autosEdit.map((x, i) => {
                    const sel = (form.autos_processo_id || autosEdit[0].processo_id) === x.processo_id;
                    return (
                      <button key={x.processo_id} type="button" data-testid={`event-edit-autos-${x.processo_id}`} aria-pressed={sel}
                        onClick={() => setForm((f) => ({ ...f, autos_processo_id: x.processo_id }))}
                        className={`min-h-[36px] px-1 rounded-full ${sel ? "ring-2 ring-stone-500 ring-offset-1" : "opacity-80"}`}>
                        <AutosTag autos={x.autos} nome={x.autos_nome} className="py-1.5 px-2.5 text-[11px]" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {!isEdit && regra?.valido && regraDigitos && (() => {
              const m = regra.monitorado;
              const dataIso = dateBrToIso(form.date);
              const nomeTipo = (a) => (a.event_type === "prazo" ? (a.deadline_type || "Prazo") : a.event_type === "reuniao" ? (a.meeting_subject || "Compromisso") : "Audiência");
              const dup = (regra.agendamentos || []).find((a) => a.event_type === form.event_type && dataIso && a.date === dataIso);
              const dd = (iso) => { const [y, mo, d] = String(iso || "").split("-"); return d ? `${d}/${mo}/${y}` : ""; };
              return (
                <div className="mt-2 rounded-md border border-[#8cc7f5] bg-[#f3f9ff] p-2.5 text-xs text-[#0b4f94] space-y-1.5" data-testid="event-regra-box">
                  {m ? (
                    <p data-testid="event-regra-monitorado">
                      <span className="font-semibold">Processo monitorado</span>{m.apelido ? ` — ${m.apelido}` : ""}.
                      {regraIntegrado.length ? ` Preenchi ${regraIntegrado.join(", ")} a partir do cadastro.` : ""}{" "}
                      <button type="button" data-testid="event-regra-abrir" onClick={() => abrirDaRegra(`/processos/${m.processo_id}`)} className="font-semibold underline underline-offset-2">Abrir ›</button>
                    </p>
                  ) : regra.bloqueado ? null : (
                    <p className="flex flex-wrap items-center gap-2" data-testid="event-regra-nao-monitorado">
                      <span>Este processo ainda não é monitorado.</span>
                      <button type="button" data-testid="event-regra-monitorar" disabled={monitorando} onClick={monitorarAgora}
                        className="min-h-[32px] px-3 rounded-full border border-[#8cc7f5] bg-white font-semibold hover:bg-[#e3f1fd] disabled:opacity-50">
                        {monitorando ? "Cadastrando…" : "Monitorar"}
                      </button>
                    </p>
                  )}
                  {(regra.monitorados || []).length > 1 && (
                    <div data-testid="event-regra-autos">
                      <p className="mb-1">Este número tem {regra.monitorados.length} autos. De qual é este compromisso?</p>
                      <div className="flex flex-wrap gap-1.5">
                        {regra.monitorados.map((x) => {
                          const sel = form.autos_processo_id === x.processo_id;
                          return (
                            <button key={x.processo_id} type="button" data-testid={`event-regra-autos-${x.processo_id}`} aria-pressed={sel}
                              onClick={() => setForm((f) => ({ ...f, autos_processo_id: x.processo_id, court: x.vara || f.court, court_district: x.comarca || f.court_district }))}
                              className={`min-h-[36px] px-1 rounded-full ${sel ? "ring-2 ring-[#0b4f94] ring-offset-1" : "opacity-85"}`}>
                              <AutosTag autos={x.autos} nome={x.autos_nome} className="py-1.5 px-2.5 text-[11px]" />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {regra.total_agendamentos > 0 && (
                    <div data-testid="event-regra-agendamentos">
                      <p>Já há {regra.total_agendamentos} {regra.total_agendamentos === 1 ? "agendamento" : "agendamentos"} neste processo:</p>
                      <ul className="mt-0.5 space-y-0.5">
                        {regra.agendamentos.slice(0, 3).map((a) => (
                          <li key={a.pub_id}>
                            <button type="button" onClick={() => abrirDaRegra(`/audiencia/${a.pub_id}`)} className="text-left underline underline-offset-2 hover:text-[#08396b]">
                              {a.autos_tag && a.autos_tag.autos !== "principal" ? `[${autosLabel(a.autos_tag.autos, a.autos_tag.autos_nome)}] ` : ""}{nomeTipo(a)} — {dd(a.date)}{a.time ? ` às ${String(a.time).slice(0, 5)}` : ""} ›
                            </button>
                          </li>
                        ))}
                        {regra.total_agendamentos > 3 && <li className="text-[#0b4f94]/80">e mais {regra.total_agendamentos - 3}</li>}
                      </ul>
                    </div>
                  )}
                  {dup && (
                    <p className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-amber-900" data-testid="event-regra-duplicado">
                      Atenção: já existe {form.event_type === "prazo" ? "um prazo" : form.event_type === "audiencia" ? "uma audiência" : "um compromisso"} neste processo em {dd(dup.date)}. Confira para não duplicar.
                    </p>
                  )}
                </div>
              );
            })()}
          </div>

          <div className="-mt-1" data-testid="event-mais-detalhes">
            {form.event_type !== "prazo" && (
              <Recolhivel
                testid="sec-video"
                icon={Video}
                titulo={form.event_type === "audiencia" ? "Link da audiência" : "Videoconferência"}
                resumo={form.meeting_url || form.meeting_id || form.meeting_password ? (form.meeting_url ? "link preenchido" : "dados da sala preenchidos") : "link, ID da sala, senha"}
                preenchido={!!(form.meeting_url || form.meeting_id || form.meeting_password)}
              >
            <div className="grid grid-cols-1 gap-3">
              <Input
                data-testid="event-meeting-url-input"
                value={form.meeting_url}
                onChange={(e) => setForm({ ...form, meeting_url: e.target.value })}
                placeholder="Link Zoom / Meet / Teams (opcional)"
              />
              <Input
                data-testid="event-meeting-id-input"
                value={form.meeting_id}
                onChange={(e) => setForm({ ...form, meeting_id: e.target.value })}
                placeholder="ID da sala (opcional)"
              />
              <Input
                data-testid="event-meeting-password-input"
                value={form.meeting_password}
                onChange={(e) => setForm({ ...form, meeting_password: e.target.value })}
                placeholder="Senha da sala (opcional)"
              />
            </div>
              </Recolhivel>
            )}

            <Recolhivel
              testid="sec-partes"
              icon={Users}
              titulo={form.event_type === "reuniao" ? "Cliente" : "Partes e cliente"}
              resumo={[form.event_type !== "reuniao" && (form.party_author || form.party_defendant) ? [form.party_author, form.party_defendant].filter(Boolean).join(" × ") : null, form.client_id !== "none" ? (clients.find((c) => c.client_id === form.client_id)?.name || "cliente vinculado") : null].filter(Boolean).join(" · ") || "opcional"}
              preenchido={!!(form.event_type !== "reuniao" && (form.party_author || form.party_defendant)) || form.client_id !== "none"}
            >
              {form.event_type !== "reuniao" && (
                <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-stone-500 mb-1.5 block">
                    {PARTY_LABELS[form.case_area][0]}
                  </Label>
                  <SugestaoInput
                    testid="event-party-author-input"
                    fonte={["partes", "clientes"]}
                    value={form.party_author}
                    onValueChange={(v) => setForm((f) => ({ ...f, party_author: v }))}
                    placeholder="Nome"
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-500 mb-1.5 block">
                    {PARTY_LABELS[form.case_area][1]}
                  </Label>
                  <SugestaoInput
                    testid="event-party-defendant-input"
                    fonte={["partes", "clientes"]}
                    value={form.party_defendant}
                    onValueChange={(v) => setForm((f) => ({ ...f, party_defendant: v }))}
                    placeholder="Nome"
                  />
                </div>
              </div>
                  <Dica className="text-[11px] text-stone-400 -mt-2">{`Aparecem no lugar da descrição na lista de próximas audiências. Se deixar em branco e vincular um cliente, usamos o nome dele.`}</Dica>
                </>
              )}
          <div>
            <Label htmlFor="event-client-select" className="text-xs text-stone-500 mb-1.5 block">Cliente vinculado</Label>
            <select
              id="event-client-select"
              data-testid="event-client-select"
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              className="w-full h-10 px-3 rounded-md border border-stone-300 bg-white text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-gold-500/30 focus:border-gold-500"
            >
              <option value="none">— Sem cliente —</option>
              {clients.map((c) => (
                <option key={c.client_id} value={c.client_id}>{c.name}</option>
              ))}
            </select>
          </div>
            </Recolhivel>

            <Recolhivel
              testid="sec-vara"
              icon={Building2}
              titulo="Vara e comarca"
              resumo={[form.court, form.court_district].filter(Boolean).join(" · ") || "opcional"}
              preenchido={!!(form.court || form.court_district)}
            >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-stone-500 mb-1.5 block">
                {form.event_type === "prazo" ? "Órgão / Vara" : "Vara"}
              </Label>
              <SugestaoInput
                testid="event-court-input"
                fonte="varas"
                value={form.court}
                onValueChange={(v) => setForm((f) => ({ ...f, court: v }))}
                placeholder="Opcional"
              />
            </div>
            <div>
              <Label className="text-xs text-stone-500 mb-1.5 block">Comarca</Label>
              <SugestaoInput
                testid="event-court-district-input"
                fonte="comarcas"
                value={form.court_district}
                onValueChange={(v) => setForm((f) => ({ ...f, court_district: v }))}
                placeholder="Opcional"
              />
            </div>
          </div>
            </Recolhivel>

            <Recolhivel
              testid="sec-lembretes"
              icon={Bell}
              titulo="Lembretes"
              resumo={`${REMINDER_OPTIONS.find((o) => o.value === form.reminder)?.label || "Nenhum"} · ligação: ${CALL_REMINDER_MODES.find((o) => o.value === form.call_reminder_mode)?.label || "Padrão"}`}
              preenchido={form.reminder !== "" || (form.event_type !== "prazo" ? form.call_reminder_mode !== "default" : form.call_reminder_mode !== "off")}
            >
          <div>
            <Label className="text-xs text-stone-500 mb-1.5 block">Lembrete</Label>
            <select
              data-testid="event-reminder-select"
              value={form.reminder}
              onChange={(e) => setForm({ ...form, reminder: e.target.value })}
              className="h-9 w-full rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-400"
            >
              {REMINDER_OPTIONS.map((o) => (
                <option key={o.value || "none"} value={o.value}>{o.label}</option>
              ))}
            </select>
            {form.reminder !== "" && form.reminder !== "daily" && (
              <p className="text-[11px] text-stone-500 mt-1">
                Você recebe uma notificação no celular/computador{form.time ? "" : " (sem horário definido, contamos a partir das 9h, ou das 18h se for prazo)"}.
              </p>
            )}
            {form.reminder === "daily" && (
              <div className="mt-2 bg-stone-50 border border-stone-200 rounded-md p-3" data-testid="event-reminder-daily-box">
                <Dica className="text-[12px] text-stone-600 mb-2" max={80}>{`
                  Avisa todo dia às 7h (notificação e e-mail) até você marcar o compromisso como
                  feito. Útil para acompanhar um andamento que precisa ser conferido todo dia.`}</Dica>
                <Label className="text-xs text-stone-500 mb-1.5 block">Termina em (opcional)</Label>
                <Input
                  data-testid="event-recurring-until-input"
                  placeholder="dd/mm/aaaa"
                  maxLength={10}
                  value={form.recurring_until}
                  onChange={(e) => setForm({ ...form, recurring_until: maskDateInput(e.target.value) })}
                  className="w-40"
                />
              </div>
            )}
          </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Phone className="w-3.5 h-3.5 text-stone-500" strokeWidth={2} />
                  <Label className="text-xs text-stone-500">Lembrete por ligação (opcional)</Label>
                </div>
            <div className="grid grid-cols-3 gap-2">
              {CALL_REMINDER_MODES.map(({ value, label }) => (
                <button
                  key={value}
                  data-testid={`call-reminder-mode-${value}`}
                  type="button"
                  onClick={() => setForm({ ...form, call_reminder_mode: value })}
                  className={`px-2 py-2 rounded-md border text-xs font-medium transition-colors ${
                    form.call_reminder_mode === value
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <Dica className="text-[11px] text-stone-400 mt-1.5">{`"Padrão" segue o que está em Configurações. "Ativar" liga só pra esse compromisso, mesmo que o canal esteja desligado. "Desativar" nunca liga pra esse, mesmo com o canal ligado.`}</Dica>
            {form.call_reminder_mode === "on" && (
              <div className="mt-3">
                <Label className="text-xs text-stone-500 mb-1.5 block">Ligar quantos minutos antes</Label>
                <Input
                  data-testid="call-reminder-minutes-input"
                  type="number" min="5" max="60"
                  value={form.call_reminder_minutes_before}
                  onChange={(e) => setForm({ ...form, call_reminder_minutes_before: e.target.value })}
                  placeholder="Padrão de Configurações (ex: 30)"
                  className="w-40"
                />
                <span className="text-[11px] text-stone-400 block mt-1">De 5 a 60 minutos. Em branco, usa o padrão de Configurações.</span>
              </div>
            )}
              </div>
            </Recolhivel>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          {/* Lote 26: o botão não trava mais a aparência/clique com base em form.date/
              form.time/form.description — fica sempre com a cor normal e clicável, e o
              submit() valida de verdade (mostra aviso claro se faltar algo). Desde o Lote 29,
              data/hora são campos de texto com máscara (não mais o input nativo de
              calendário), então o problema original de sincronia nem existe mais. */}
          <Button
            data-testid="submit-event-button"
            onClick={submit}
            disabled={saving}
            className="bg-emerald-brand text-white gap-2"
          >
            {isEdit ? <Pencil className="w-4 h-4" strokeWidth={2} /> : <CalendarPlus className="w-4 h-4" strokeWidth={2} />}
            {saving ? "Salvando…" : isEdit ? "Salvar alterações" : "Salvar e agendar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Cores das etiquetas de tipo de evento (Lote 56, pedido do Robson, 30/09/2026): "prazo"
// em cinza um pouco mais escuro com fonte preta negrito, "audiência" em dourado escuro
// (a mesma cor de marca usada em bg-emerald-brand) também com fonte preta negrito — as
// duas centralizadas, mesmo padrão de pílula de antes. EventTypeBadge é o único lugar que
// desenha essa etiqueta — usado em Calendar.jsx, HearingsList.jsx, Dashboard.jsx e
// HearingDetail.jsx — então a troca de cor vale automaticamente em todo lugar que o evento
// aparece, sem precisar repetir em cada tela.
export function EventTypeBadge({ hearing, passou = false }) {
  const type = hearing?.event_type || "audiencia";
  const label = TYPES.find((t) => t.value === type)?.label || "Audiência";
  // Lote 112: compromisso mostra o subtipo (Perícia, Reunião, Lembrete ou o texto digitado) com cor própria.
  const sub = type === "reuniao" ? subtipoCompromisso(hearing) : null;
  const color = {
    audiencia: "bg-gold-500 text-black border border-gold-600",
    reuniao: sub ? SUBTIPO_COR[sub.key] : SUBTIPO_COR.compromisso,
    prazo: "bg-stone-300 text-black border border-stone-400",
  }[type];
  // Lote 151 (Robson, 01/11/2026): mesmo formato da etiqueta "Audiência agendada" (pílula com ícone, texto normal),
  // mantendo as cores de cada tipo.
  const Icone = type === "audiencia" ? Gavel : type === "prazo" ? Clock3
    : sub?.key === "pericia" ? FileSearch : sub?.key === "lembrete" ? Bell : Users;
  return (
    <span data-testid="event-type-badge" className={`inline-flex items-center justify-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${passou ? "bg-stone-100 text-stone-400 border border-stone-200" : color}`}>
      <Icone className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} aria-hidden="true" />
      {sub ? sub.label : label}
    </span>
  );
}
